import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import axios from 'axios';
import { Building2, Calendar, FileText, Loader2, User as UserIcon } from 'lucide-react';
import { EventService } from '@/services/EventService';
import { SharedEvent, SharedEventAttachment } from '@/types';
import { isImageMedia, isVideoMedia } from '@/lib/media/mediaTypes';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; kind: 'invalid' | 'generic' }
  | { status: 'ready'; data: SharedEvent };

const isImage = (a: SharedEventAttachment) =>
  isImageMedia({ type: a.type, mimeType: a.mimeType });
const isVideo = (a: SharedEventAttachment) =>
  isVideoMedia({ type: a.type, mimeType: a.mimeType });

const safeFormatDate = (value?: string) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return format(d, 'd בMMMM yyyy', { locale: he });
};

const safeFormatDateTime = (value?: string) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return format(d, 'd בMMMM yyyy HH:mm', { locale: he });
};

export default function SharedEventView() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmitAnswer = async () => {
    const trimmed = answer.trim();
    if (!trimmed || submitting || submitted || !token) return;
    setSubmitting(true);
    try {
      await EventService.answerSharedEvent(token, trimmed);
      toast({ title: 'התשובה נשלחה בהצלחה' });
      setAnswer('');
      setSubmitted(true);
    } catch {
      toast({ title: 'שליחת התשובה נכשלה', variant: 'destructive' });
      // answer text preserved; submitted stays false → retry allowed
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', kind: 'invalid' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    EventService.getSharedEvent(token)
      .then((data) => {
        if (cancelled) return;
        setState({ status: 'ready', data });
      })
      .catch((err) => {
        if (cancelled) return;
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        const kind: 'invalid' | 'generic' = status === 404 || status === 410 ? 'invalid' : 'generic';
        setState({ status: 'error', kind });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const groupedAttachments = useMemo(() => {
    if (state.status !== 'ready') return { images: [], videos: [], documents: [] };
    const all = state.data.attachments || [];
    return {
      images: all.filter(isImage),
      videos: all.filter(isVideo),
      documents: all.filter((a) => !isImage(a) && !isVideo(a)),
    };
  }, [state]);

  if (state.status === 'loading') {
    return (
      <div dir="rtl" className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">טוען תיעוד...</p>
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    const message =
      state.kind === 'invalid'
        ? 'הקישור אינו תקף או שפג תוקפו'
        : 'לא ניתן לטעון את התיעוד כרגע';
    return (
      <div dir="rtl" className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 text-center">
          <p className="text-lg font-medium text-foreground">{message}</p>
        </div>
      </div>
    );
  }

  const { data } = state;
  const formattedDate = safeFormatDate(data.date);
  const formattedCreatedAt = safeFormatDateTime(data.createdAt);
  // today: purely client-side. Later: `|| Boolean(data.answeredAt)` etc.
  const isAnswered = submitted;

  return (
    <div dir="rtl" className="min-h-screen bg-background">
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6 pb-safe">
        <header className="space-y-3">
          <h1 className="text-2xl font-bold leading-tight">{data.title}</h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {formattedDate && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formattedDate}
              </span>
            )}
            {data.projectName && (
              <span className="inline-flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                {data.projectName}
              </span>
            )}
            {data.createdBy && (
              <span className="inline-flex items-center gap-1">
                <UserIcon className="w-4 h-4" />
                {data.createdBy}
              </span>
            )}
          </div>
        </header>

        {data.description && (
          <section className="bg-card border border-border rounded-2xl p-4">
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{data.description}</p>
          </section>
        )}

        {data.tags && data.tags.length > 0 && (
          <section className="flex flex-wrap gap-2">
            {data.tags.map((tag, i) => (
              <span
                key={`${tag}-${i}`}
                className="px-3 py-1 bg-accent text-accent-foreground rounded-full text-sm"
              >
                {tag}
              </span>
            ))}
          </section>
        )}

        {groupedAttachments.images.length > 0 && (
          <section>
            <h2 className="font-medium mb-3 text-sm text-muted-foreground">
              תמונות ({groupedAttachments.images.length})
            </h2>
            <div className="grid grid-cols-2 gap-2">
              {groupedAttachments.images.map((img, i) => (
                <a
                  key={img.id ?? `img-${i}`}
                  href={img.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="aspect-square bg-secondary rounded-lg overflow-hidden block hover:opacity-90 transition-opacity"
                >
                  <img
                    src={img.url}
                    alt={img.filename || 'תמונה'}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          </section>
        )}

        {groupedAttachments.videos.length > 0 && (
          <section>
            <h2 className="font-medium mb-3 text-sm text-muted-foreground">
              סרטונים ({groupedAttachments.videos.length})
            </h2>
            <div className="space-y-3">
              {groupedAttachments.videos.map((vid, i) => (
                <video
                  key={vid.id ?? `vid-${i}`}
                  controls
                  preload="metadata"
                  src={vid.url}
                  className="w-full rounded-lg bg-black"
                />
              ))}
            </div>
          </section>
        )}

        {groupedAttachments.documents.length > 0 && (
          <section>
            <h2 className="font-medium mb-3 text-sm text-muted-foreground">
              קבצים ({groupedAttachments.documents.length})
            </h2>
            <ul className="space-y-2">
              {groupedAttachments.documents.map((doc, i) => (
                <li
                  key={doc.id ?? `doc-${i}`}
                  className="flex items-center justify-between gap-3 bg-card border border-border rounded-xl p-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm truncate" title={doc.filename}>
                      {doc.filename || 'קובץ מצורף'}
                    </span>
                  </div>
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={doc.filename}
                    className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity flex-shrink-0"
                  >
                    פתח
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        {(data.type === 'progress' || data.type === 'fault') && (
          <section className="bg-card border border-border rounded-2xl p-4">
            {isAnswered ? (
              <p className="text-sm font-medium text-foreground text-center">
                התשובה נשלחה בהצלחה
              </p>
            ) : (
              <div className="space-y-3">
                <h2 className="font-medium text-sm text-foreground">מענה לשאלה</h2>
                <Textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="כתוב את תשובתך כאן..."
                  disabled={submitting}
                />
                <Button
                  onClick={handleSubmitAnswer}
                  disabled={submitting || !answer.trim()}
                  className="w-full"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  שלח תשובה
                </Button>
              </div>
            )}
          </section>
        )}

        {formattedCreatedAt && (
          <footer className="text-xs text-muted-foreground text-center pt-4 border-t border-border">
            נוצר ב-{formattedCreatedAt}
          </footer>
        )}
      </main>
    </div>
  );
}
